import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { SEO_STYLES } from "@/lib/seoStyles";
import { SEO_CITIES, SEO_DISTRICTS_BY_CITY } from "@/lib/seoCities";

/**
 * PopularSearchesGrid — interne Verlinkung für SEO + Discovery.
 *
 * @param {Object} props
 * @param {string} [props.currentStyle] - aktueller Stil-Slug
 * @param {string} [props.currentCity] - aktuelle Stadt-Slug
 * @param {string} [props.currentDistrict] - aktueller Bezirk-Slug (optional)
 * @param {"de"|"en"} props.lang
 * @param {string} props.headingDe
 * @param {string} props.headingEn
 * @param {string} [props.subDe]
 * @param {string} [props.subEn]
 * @param {"city"|"style"|"mixed"|"sibling-districts"} [props.mode="mixed"] -
 *   "city": zeige alle Stile in der currentCity
 *   "style": zeige currentStyle in allen anderen Städten
 *   "sibling-districts": zeige currentStyle in anderen Vierteln der currentCity
 *   "mixed": Best-of beider
 * @param {number} [props.limit=24]
 */
export default function PopularSearchesGrid({
  currentStyle,
  currentCity,
  currentDistrict,
  lang,
  headingDe,
  headingEn,
  subDe,
  subEn,
  mode = "mixed",
  limit = 24,
}) {
  const links = useMemo(() => {
    const out = [];

    if (mode === "city" && currentCity) {
      // Alle Stile in dieser Stadt (außer currentStyle)
      const city = SEO_CITIES.find((c) => c.slug === currentCity);
      if (city) {
        SEO_STYLES.forEach((s) => {
          if (s.slug === currentStyle) return;
          out.push({
            to: `/${s.slug}/${city.slug}`,
            title: `${lang === "de" ? s.shortDe : s.shortEn} ${lang === "de" ? city.nameDe : city.nameEn}`,
            sub: lang === "de" ? s.labelDe : s.labelEn,
          });
        });
      }
    } else if (mode === "style" && currentStyle) {
      // Dieser Stil in allen anderen Städten
      const style = SEO_STYLES.find((s) => s.slug === currentStyle);
      if (style) {
        SEO_CITIES.forEach((c) => {
          if (c.slug === currentCity) return;
          out.push({
            to: `/${style.slug}/${c.slug}`,
            title: `${lang === "de" ? style.shortDe : style.shortEn} ${lang === "de" ? c.nameDe : c.nameEn}`,
            sub: lang === "de" ? c.nameDe : c.nameEn,
          });
        });
      }
    } else if (mode === "sibling-districts" && currentStyle && currentCity) {
      // Gleicher Stil + gleiche Stadt, andere Viertel
      const style = SEO_STYLES.find((s) => s.slug === currentStyle);
      const city = SEO_CITIES.find((c) => c.slug === currentCity);
      const districts = SEO_DISTRICTS_BY_CITY[currentCity] || [];
      if (style && city) {
        districts.forEach((d) => {
          if (d.slug === currentDistrict) return;
          const distName = lang === "de" ? d.nameDe : d.nameEn;
          const cityName = lang === "de" ? city.nameDe : city.nameEn;
          out.push({
            to: `/${style.slug}/${city.slug}/${d.slug}`,
            title: `${lang === "de" ? style.shortDe : style.shortEn} ${distName}`,
            sub: `${cityName}-${distName}`,
          });
        });
      }
    } else {
      // Mixed: Top-12 Städte × Top-3 Stile, dann Diversität
      const topCities = SEO_CITIES.slice(0, 12);
      const topStyles = ["bio", "vegan", "glutenfrei", "regional", "vegetarisch", "paleo"];
      topCities.forEach((c) => {
        topStyles.forEach((sSlug) => {
          if (sSlug === currentStyle && c.slug === currentCity) return;
          const s = SEO_STYLES.find((x) => x.slug === sSlug);
          if (!s) return;
          out.push({
            to: `/${s.slug}/${c.slug}`,
            title: `${lang === "de" ? s.shortDe : s.shortEn} ${lang === "de" ? c.nameDe : c.nameEn}`,
            sub: lang === "de" ? c.nameDe : c.nameEn,
          });
        });
      });
    }

    return out.slice(0, limit);
  }, [mode, currentStyle, currentCity, currentDistrict, lang, limit]);

  if (!links.length) return null;

  return (
    <section
      className="section-pad-sm border-t border-soft"
      data-testid="popular-searches"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
        <h2 className="display-md mb-3" data-testid="popular-searches-heading">
          {lang === "de" ? headingDe : headingEn}
        </h2>
        {(subDe || subEn) && (
          <p className="body-md text-muted max-w-2xl mb-8">
            {lang === "de" ? subDe : subEn}
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {links.map((l, i) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`popular-search-${i}`}
              className="block px-4 py-3 border border-soft rounded-sm hover:border-primary transition-colors group"
            >
              <span className="block text-[14px] text-primary group-hover:text-accent transition-colors leading-snug">
                {l.title}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
